import React, { useState, useRef, useEffect } from 'react';

interface DatePickerProps {
  selectedDate: string; // YYYY-MM-DD format
  onChange: (date: string) => void;
  minDate?: string; // YYYY-MM-DD format
  maxDate?: string; // YYYY-MM-DD format
  className?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ 
  selectedDate, 
  onChange, 
  minDate,
  maxDate,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => {
    // Initialize with selected date or today
    if (selectedDate) {
      const [year, month] = selectedDate.split('-').map(Number);
      return new Date(year, month - 1, 1);
    }
    return new Date();
  });
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const today = new Date();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateSelected = (date: Date) => {
    return selectedDate === formatDate(date);
  };

  const handleDateClick = (date: Date) => {
    const dateStr = formatDate(date);
    onChange(dateStr);
    setIsOpen(false); // Close dropdown after selection
  };

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return 'Select date';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }).format(date);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for week alignment
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ date: null, isCurrentMonth: false, isEmpty: true });
    }

    // Add dates for the current month
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      days.push({ date: currentDate, isCurrentMonth: true, isEmpty: false });
    }

    return days;
  };

  const getMonthName = (date: Date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[date.getMonth()];
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isDateSelectable = (date: Date) => {
    const dateStr = formatDate(date);
    const todayStr = formatDate(today);
    
    // Check minimum date (default: today)
    const minDateStr = minDate || todayStr;
    if (dateStr < minDateStr) {
      return false;
    }
    
    // Check maximum date (default: today + 30 days)
    if (maxDate && dateStr > maxDate) {
      return false;
    }
    
    return true;
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full border border-gray-300 rounded-md px-3 md:px-4 py-3 pr-10 text-sm md:text-base text-left focus:outline-none focus:ring-2 focus:ring-green-500 bg-white min-h-[48px] cursor-pointer"
        >
          {formatDisplayDate(selectedDate)}
        </button>
        {/* 自定义箭头图标 */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown Calendar */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 md:right-auto md:w-80 z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-md text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              ←
            </button>
            <h3 className="text-lg font-medium text-gray-800">
              {getMonthName(currentDate)} {currentDate.getFullYear()}
            </h3>
            <button
              type="button"
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-md text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              →
            </button>
          </div>

          {/* Day of week headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2 w-10">
                {day}
              </div>
            ))}
          </div>

          {/* Date grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              if (day.isEmpty || !day.date) {
                return <div key={index} className="h-10 w-10"></div>;
              }

              const date = day.date;
              const isSelected = isDateSelected(date);
              const isToday = date.toDateString() === today.toDateString();
              const isSelectable = isDateSelectable(date);

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => isSelectable && handleDateClick(date)}
                  disabled={!isSelectable}
                  className={`
                    h-10 w-10 rounded-md text-sm transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center
                    ${!day.isCurrentMonth 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : isSelectable
                        ? isSelected 
                          ? 'bg-green-600 text-white hover:bg-green-700 font-medium' 
                          : 'text-gray-700 hover:bg-gray-100'
                        : 'text-gray-300 cursor-not-allowed'
                    }
                    ${isToday && day.isCurrentMonth ? 'border border-green-400' : ''}
                    focus:outline-none focus:ring-2 focus:ring-green-500
                  `}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;